import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { injectedLocalApi } from '@/services/local'
import { injectedOmsApi } from '@/services/oms'
import { appendSelectedItems } from '@/utils/promotion'
import { APPLIED_PROMO_FREE_ITEM_KEY_NAME } from '@/libs/config/client'

type PlainInfo<T> = {
  status: RequestState
  error?: string
  data: T
}

type RequestState = 'pending' | 'fulfilled' | 'rejected' | 'none'

interface OrderState {
  storeOrder: PlainInfo<Oms.StoreOrder>
  eligiblePromotions: PlainInfo<Oms.EligiblePromotions>
  selectedPromotions: PlainInfo<SelectedPromotion[]>
  availableTimeSlots: PlainInfo<Oms.AvailableTimeSlots[]>
  updatingQuantity: PlainInfo<{
    orderId: string | null
  }>
}

const initialState: OrderState = {
  storeOrder: {
    status: 'none',
    data: {} as Oms.StoreOrder,
  },
  eligiblePromotions: {
    status: 'none',
    data: {} as Oms.EligiblePromotions,
  },
  selectedPromotions: {
    status: 'none',
    data: [] as SelectedPromotion[],
  },
  availableTimeSlots: {
    status: 'none',
    data: [] as Oms.AvailableTimeSlots[],
  },
  updatingQuantity: {
    status: 'none',
    data: {
      orderId: null,
    },
  },
}

export const orderSlice = createSlice({
  name: '@order',
  initialState,
  reducers: {
    togglePromotion: (
      state,
      action: PayloadAction<{ promotion: SelectedPromotion }>,
    ) => {
      const promotion = action.payload.promotion
      const idx = state.selectedPromotions.data.findIndex(
        (p) => p.id === promotion.id,
      )
      if (idx < 0) {
        if (promotion.isPromoCodePromotion && promotion.stackableOption) {
          state.selectedPromotions.data = state.selectedPromotions.data.filter(
            (p) => p.isPromoCodePromotion,
          )
        }
        state.selectedPromotions.data.push(promotion)
      } else {
        state.selectedPromotions.data.splice(idx, 1)
      }
    },
    setIsUpdatingQuantity: (
      state,
      action: PayloadAction<{
        status: 'fulfilled' | 'rejected' | 'none' | 'pending'
        orderId: string | null
      }>,
    ) => {
      state.updatingQuantity.status = action.payload.status
      state.updatingQuantity.data.orderId = action.payload.orderId
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        injectedLocalApi.endpoints.getStoreOrder.matchFulfilled,
        (state, action) => {
          // OMS answers business failures (e.g. errorCode=access_denied when
          // the cart belongs to a different customer than the cookies name)
          // with HTTP 200 + result:false and no payload.storeOrder. Treat that
          // as rejected rather than storing `undefined` as the cart, which
          // crashed the first consumer to read a field off it.
          const storeOrder = action.payload?.data?.payload?.storeOrder
          if (!storeOrder) {
            state.storeOrder.status = 'rejected'
            return
          }
          state.storeOrder.status = 'fulfilled'
          state.storeOrder.data = storeOrder
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.getStoreOrder.matchPending,
        (state) => {
          state.storeOrder.status = 'pending'
        },
      )
      // Without this, a rejected getStoreOrder left status stuck at 'pending'
      // forever (matchPending set it, nothing cleared it). That is the
      // "Add button stuck loading / buttons unresponsive" report: when a
      // returning customer's stored storeOrderId has an expired collection
      // date, OMS answers ajax-retrieve-storeOrder with no_available_dates,
      // the request rejects, and every control gated on
      // storeOrder.status === 'pending' (cart button, add, quantity) spins
      // and stays disabled. Incognito escaped it because a fresh session has
      // no stored storeOrderId, so the query is skipped entirely. Move to
      // 'rejected' so the UI leaves the loading state and falls back to a
      // usable path (e.g. re-prompt dining preference / date).
      .addMatcher(
        injectedLocalApi.endpoints.getStoreOrder.matchRejected,
        (state, action) => {
          // Ignore aborted rejections (query arg changed / component
          // unmounted mid-flight): those are not real failures and shouldn't
          // flip a healthy pending into rejected.
          if (action.meta.aborted) return
          state.storeOrder.status = 'rejected'
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.updatePickupLocation.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.syncOrderMethod.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateDeliveryLocation.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateDeliveryAddressTwo.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload.data?.payload?.address) {
            state.storeOrder.data.deliveryAddress =
              action.payload.data.payload.address
            state.storeOrder.data.deliveryAddressTwo =
              action.payload.data.payload.addressTwo
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateCollectionDateTime.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        },
      )
      .addMatcher(
        injectedOmsApi.endpoints.getTimeSlots.matchFulfilled,
        (state, action) => {
          state.availableTimeSlots.status = 'fulfilled'
          if (action.payload?.payload?.timeIntervals) {
            state.availableTimeSlots.data = action.payload.payload.timeIntervals
          }
        },
      )
      .addMatcher(
        injectedOmsApi.endpoints.getTimeSlots.matchPending,
        (state) => {
          state.availableTimeSlots.status = 'pending'
          state.availableTimeSlots.data = []
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.addItemToCart.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload?.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateStoreOrderItemQuantity.matchPending,
        (state, action) => {
          state.storeOrder.status = 'pending'
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateStoreOrderItemQuantity.matchFulfilled,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload?.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.updateStoreOrderItemQuantity.matchRejected,
        (state, action) => {
          state.storeOrder.status = 'fulfilled'
        },
      )
      .addMatcher(
        injectedOmsApi.endpoints.getEligiblePromotions.matchPending,
        (state) => {
          state.eligiblePromotions.status = 'pending'
        },
      )
      .addMatcher(
        injectedOmsApi.endpoints.getEligiblePromotions.matchFulfilled,
        (state, action) => {
          state.eligiblePromotions.status = 'fulfilled'
          state.eligiblePromotions.data = action.payload.payload
          if (state.storeOrder.data.orderPromotions) {
            state.selectedPromotions.data = appendSelectedItems(
              state.eligiblePromotions.data.promotions,
              state.storeOrder.data.orderPromotions,
              state.storeOrder.data.orderItems,
            ).concat(
              appendSelectedItems(
                state.eligiblePromotions.data.promoCodePromotions,
                state.storeOrder.data.orderPromotions,
                state.storeOrder.data.orderItems,
              ),
            )
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.clearCart.matchFulfilled,
        (state, action) => {
          if (action.payload?.data?.payload?.storeOrder) {
            state.storeOrder.data = action.payload.data.payload.storeOrder
          }
        },
      )
      .addMatcher(
        injectedLocalApi.endpoints.applyPromotions.matchFulfilled,
        (state, action) => {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(
              'lastTimeTouchOrder',
              Date.now().toString(),
            )
          }
          if (action.payload?.data?.payload?.storeOrder) {
            window.localStorage.removeItem(APPLIED_PROMO_FREE_ITEM_KEY_NAME)
          }
        },
      )
  },
})

export const { togglePromotion, setIsUpdatingQuantity } = orderSlice.actions
